import React, {useContext, useEffect, useRef} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Button, {atomButtonDesigns} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useOnScreen} from '@s-ui/react-hooks'
import CardDealerService from '@adv-mt/mt-card-dealer-service'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {ARRAY_OFFSET, BASE_CLASS, CONTEXTS as sectionServicesContext, TRACKS} from './config.js'

const getOrderedServices = ({services, servicesOrder}) =>
  services.sort((serviceA, serviceB) => {
    const {type: typeServiceA} = serviceA
    const {type: typeServiceB} = serviceB

    // if service is not on services list provided by domain, place it at the end of the array
    if (servicesOrder.indexOf(typeServiceA) === -1) return 1

    return servicesOrder.indexOf(typeServiceA) - servicesOrder.indexOf(typeServiceB)
  })

function SectionServicesContent({
  context = sectionServicesContext.DETAIL,
  onButtonClick = () => {},
  onServiceClick = () => {},
  services = []
}) {
  const ref = useRef()
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const servicesOrder = config.get('DEALER_SERVICES_ORDER')
  const {track} = useAnalyticsTrack()
  const [isIntersecting] = useOnScreen({ref, once: true})
  const servicesItem = services.length

  const isSubscriptionContext =
    context === sectionServicesContext.DETAIL_SUBSCRIPTION || context === sectionServicesContext.STOCK_SUBSCRIPTION

  const classServicesContent = cx(`${BASE_CLASS}-content`, {
    [`${BASE_CLASS}-content--panel`]: isSubscriptionContext
  })

  const classServiceContentItem = cx(`${BASE_CLASS}-service`, {
    [`${BASE_CLASS}-service--hidden`]: !isSubscriptionContext
  })

  const textSectionTitle = isSubscriptionContext
    ? i18n.t('DEALER_SERVICE.DEALER_SERVICES_SUBSCRIPTION_TITLE')
    : i18n.t('DEALER_SERVICE.DEALER_SERVICES_TITLE')

  const textSectionButton = isSubscriptionContext ? i18n.t('COMMON.KNOW_MORE') : i18n.t('COMMON.ALL')

  const orderedServices = getOrderedServices({services, servicesOrder})

  const servicesList = orderedServices.map((service, index) => {
    const currentItem = index + ARRAY_OFFSET
    const isLastItem = servicesItem === currentItem
    const refProp = {
      ...(isLastItem && {ref})
    }

    return (
      <div className={classServiceContentItem} key={service.type} onClick={onServiceClick} {...refProp}>
        {isSubscriptionContext ? (
          <CardDealerService
            id={service.type}
            type="compact"
            service={service.title}
            description={service.description}
            isIncluded={service.isIncluded}
          />
        ) : (
          <CardDealerService
            id={service.type}
            type="brief"
            service={service.title}
            description={service.description}
            isIncluded={service.isIncluded}
          />
        )}
      </div>
    )
  })

  useEffect(() => {
    isIntersecting && track(TRACKS.SERVICES_SEEN)
  }, [isIntersecting, track])

  return (
    <>
      <div className={`${BASE_CLASS}-title`}>
        <TitleBasic as={titleBasicTags.h2} title={textSectionTitle} size={titleBasicSizes.xs} />
        <Button link onClick={onButtonClick} design={atomButtonDesigns.LINK} role="button">
          {textSectionButton}
        </Button>
      </div>
      <div className={classServicesContent}>{servicesList}</div>
    </>
  )
}

SectionServicesContent.displayName = 'SectionServicesContent'
SectionServicesContent.propTypes = {
  context: PropTypes.string,
  onButtonClick: PropTypes.func,
  onServiceClick: PropTypes.func,
  services: PropTypes.array.isRequired
}
export default React.memo(SectionServicesContent)
